import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { formatBlockSummary, type TTooltipPlotLine } from 'App/dashboards/_utils/buildPestTooltip'
import type { SeriesOptionsType } from 'highcharts/highstock'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { escapeHtml } from 'utils/escapeHtml'
import { ALMOND_BLOOM_KEY, ALMOND_HULL_SPLIT_KEY } from './pestValueTypeKeys'

const CROP_EVENT_COLOR = colors.purple
const CROP_EVENT_ID_PREFIX = 'DASHBOARD_CROP_EVENT_'

// The on-chart label uses `label`. The shared-tooltip row uses `tooltipText` when given (e.g. a
// fuller list than the compact on-chart "+N" label), else `label`; with blockNames it appends the
// "{n} blocks: …" summary, matching the applied-sprays row.
const makePlotLine = (
  ms: number,
  idSuffix: string,
  label: string,
  { blockNames, tooltipText }: { blockNames?: string[]; tooltipText?: string } = {},
): TTooltipPlotLine => ({
  id: `${CROP_EVENT_ID_PREFIX}${idSuffix}`,
  value: ms,
  color: CROP_EVENT_COLOR,
  width: 2,
  dashStyle: 'Dash',
  zIndex: 3,
  label: {
    text: escapeHtml(label),
    rotation: 90,
    align: 'left',
    verticalAlign: 'top',
    style: { color: CROP_EVENT_COLOR, fontSize: '11px' },
  },
  tooltipDisplay: () => {
    // label/tooltipText can contain cultivar names → escape for the useHTML tooltip (the on-chart
    // `label.text` above is escaped too). The block summary is already-escaped HTML from
    // formatBlockSummary, so it's appended as-is.
    const summary = blockNames ? formatBlockSummary(blockNames) : ''

    return `${escapeHtml(tooltipText ?? label)}${summary ? ` — ${summary}` : ''}`
  },
})

/**
 * Vertical crop-event markers for the NOW chart:
 *   - 1% Bloom: block-scoped — one line per unique 1% bloom date (almondBloom metadata.overrideDate),
 *     tooltip listing the blocks with that date.
 *   - Hull Split: property-scoped — one line per cultivar (almondHullSplit value = ISO date).
 *   - Full Bloom: property-scoped — one line per date (almondHullSplit metadata.fullBloomDate),
 *     labeled by cultivar with "+N" when several share a date; depends on hull-split data.
 * Lines are absent when the data wasn't requested/permitted (caller gates the fetch).
 */
export const buildCropEventPlotLines = ({
  data,
  propertyId,
  blockIds,
  blockNamesById,
  cultivarNamesById,
  timezone,
}: {
  data: routes.Values.Response | null
  propertyId: number | null
  blockIds: number[]
  blockNamesById: Record<number, string>
  cultivarNamesById: Record<number, string>
  timezone: string
}): { plotLines: TTooltipPlotLine[]; legendSeries: SeriesOptionsType | null } => {
  const plotLines: TTooltipPlotLine[] = []

  // 1% Bloom — one line per unique 1% bloom date, listing the blocks with that date. A block's date
  // is the user-entered override (Edit-Bloom-Settings date) when set; otherwise the model-predicted
  // date, which isn't a stored field — it's the first day the bloom-% curve reaches 1% (value is a
  // percentage, so >= 1). Showing both was the product decision (DEV-3889, Grace 2026-06-11).
  const blocks = data?.blocks as
    | Record<
        number,
        {
          values?: Record<
            string,
            {
              metadata?: { overrideDate?: string | null }
              timeseries?: { timestamp: string; value: number | null }[]
            }[]
          >
        }
      >
    | undefined

  const bloomBlocksByDay = new Map<number, Set<number>>()

  blockIds.forEach((blockId) => {
    const entry = blocks?.[blockId]?.values?.[ALMOND_BLOOM_KEY]?.[0]

    const bloomDate =
      entry?.metadata?.overrideDate ??
      entry?.timeseries?.find((point) => (point.value ?? 0) >= 1)?.timestamp ??
      null

    if (!bloomDate) return

    const ms = +moment.tz(bloomDate, timezone).startOf('day')
    const blocksForDay = bloomBlocksByDay.get(ms) ?? new Set<number>()

    blocksForDay.add(blockId)

    bloomBlocksByDay.set(ms, blocksForDay)
  })

  Array.from(bloomBlocksByDay.entries()).forEach(([ms, blockIdSet]) => {
    const blockNames = Array.from(blockIdSet)
      .map((id) => blockNamesById[id])
      .filter(Boolean)
      .sort(alphabeticalSort)

    plotLines.push(makePlotLine(ms, `bloom_${ms}`, translate.phrases.banyanApp('1% Bloom'), { blockNames }))
  })

  // Hull Split + Full Bloom — both come from the property-scoped almondHullSplit entries (one per
  // cultivar: value = hull-split ISO date, metadata.fullBloomDate = the cultivar's full bloom).
  // Block scope isn't used: the API returns every property cultivar for every block, inflating the
  // chart with no real cultivar→block attribution.
  const properties = data?.properties as
    | Record<
        number,
        {
          values?: Record<
            string,
            {
              timeseries?: { value: string | null }[]
              metadata?: { cultivarId?: number; fullBloomDate?: string | null }
            }[]
          >
        }
      >
    | undefined

  if (propertyId !== null) {
    const hullSplitEntries = properties?.[propertyId]?.values?.[ALMOND_HULL_SPLIT_KEY] ?? []
    // Full bloom: collect cultivar names per date so cultivars sharing a date render as one line
    // (with "+N") instead of stacking overlapping labels.
    const fullBloomCultivarsByDay = new Map<number, string[]>()

    hullSplitEntries.forEach((entry) => {
      const isoDate = entry.timeseries?.[0]?.value
      const cultivarId = entry.metadata?.cultivarId
      const fullBloomDate = entry.metadata?.fullBloomDate
      const cultivarName = cultivarId !== undefined ? cultivarNamesById[cultivarId] : undefined

      if (fullBloomDate) {
        const ms = +moment.tz(fullBloomDate, timezone).startOf('day')
        const names = fullBloomCultivarsByDay.get(ms) ?? []

        if (cultivarName) names.push(cultivarName)

        fullBloomCultivarsByDay.set(ms, names)
      }

      // Hull Split — one line per cultivar (value is the hull-split ISO date).
      if (isoDate) {
        const hullSplitLabel = cultivarName
          ? translate.phrases.banyanApp('{{cultivar}} Hull Split', { cultivar: cultivarName })
          : translate.phrases.banyanApp('Hull Split')

        plotLines.push(
          makePlotLine(+moment.tz(isoDate, timezone), `hull_${cultivarId ?? isoDate}`, hullSplitLabel),
        )
      }
    })

    // Full Bloom — one line per date. With multiple cultivars on a date, the on-chart label shows
    // "{first} Full Bloom +N" and the tooltip lists them all.
    Array.from(fullBloomCultivarsByDay.entries()).forEach(([ms, rawNames]) => {
      const names = rawNames.sort(alphabeticalSort)
      const fullBloom = translate.phrases.banyanApp('Full Bloom')

      let label = fullBloom
      let tooltipText: string | undefined

      if (names.length === 1) {
        label = translate.phrases.banyanApp('{{cultivar}} Full Bloom', { cultivar: names[0] })
      } else if (names.length > 1) {
        label = `${translate.phrases.banyanApp('{{cultivar}} Full Bloom', { cultivar: names[0] })} +${
          names.length - 1
        }`

        tooltipText = `${fullBloom} — ${names.join(', ')}`
      }

      plotLines.push(makePlotLine(ms, `fullbloom_${ms}`, label, { tooltipText }))
    })
  }

  if (plotLines.length === 0) return { plotLines: [], legendSeries: null }

  // Single "Crop Events" legend toggle controlling every crop-event line.
  const legendSeries: SeriesOptionsType = {
    type: 'line',
    id: 'dashboard-crop-events',
    name: translate.phrases.banyanApp('Crop Events'),
    color: CROP_EVENT_COLOR,
    dashStyle: 'Dash',
    data: [],
    marker: { enabled: false },
    events: {
      legendItemClick: function () {
        const xAxis = this.chart.xAxis?.[0]

        if (!xAxis) return

        const willHide = this.visible

        if (willHide) {
          plotLines.forEach((pl) => pl.id && xAxis.removePlotLine(pl.id))
        } else {
          plotLines.forEach((pl) => xAxis.addPlotLine(pl))
        }
      },
    },
  }

  return { plotLines, legendSeries }
}
